import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../providers/auth_provider';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { formatDate } from '../../../utils/format_date';
import { toast } from 'react-toastify';

const Payouts = () => {
    const { token } = useAuth();
    const [payouts, setPayouts] = useState([]);
    const [pagination, setPagination] = useState({});
    const [currentPage, setCurrentPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [searchPhone, setSearchPhone] = useState('');
    const [searchStatus, setSearchStatus] = useState('pending');
    const [searchName, setSearchName] = useState('');  // Adding state for Name
    const [searchEmail, setSearchEmail] = useState(''); // Adding state for Email
    const [selectedPayout, setSelectedPayout] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [selectedPayouts, setSelectedPayouts] = useState(new Set());
    const [isProcessing, setIsProcessing] = useState(false);
    const [confirmRelease, setConfirmRelease] = useState(false);
    const [payoutAmounts, setPayoutAmounts] = useState({});
    const [pendingPayoutAmounts, setPendingPayoutAmounts] = useState(0);
    const [loading, setLoading] = useState(false);

    // Initial fetch
    useEffect(() => {
        fetchPayouts(currentPage, searchStatus, searchPhone, searchName, searchEmail);
    }, [currentPage, limit, searchStatus, searchPhone, searchName, searchEmail]);

    useEffect(() => {
        if (token) {
            fetchPayoutAmounts();
        }
    }, [token])
    // Fetch payouts
    const fetchPayouts = async (page, status, phone, name, email) => {
        setLoading(true);
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/get/all/payouts`, {
                params: {
                    page,
                    limit,
                    status,
                    phone,
                    name,     // Send name as part of the query
                    email,    // Send email as part of the query
                },
                headers: {
                    Signature: `${process.env.REACT_APP_API_SIGNATURE}`,
                    Authorization: `Bearer ${token}`,
                },
            });
            setPayouts(response.data.data);
            setPagination(response.data.pagination);
        } catch (error) {
            console.error('Error fetching payouts:', error);
        } finally {
            setLoading(false);
        }
    };

    // Bulk selection
    const handleSelectAll = (e) => {
        if (e.target.checked) {
            const pendingPayouts = payouts.filter(payout => payout.payout_status === 'pending');
            const pendingPayoutIds = pendingPayouts.map(payout => payout.id);
            const pendingPayoutAmounts = pendingPayouts.reduce((sum, payout) => sum + payout.amount, 0);
            setSelectedPayouts(new Set(pendingPayoutIds));
            setPendingPayoutAmounts(pendingPayoutAmounts);
        } else {
            setSelectedPayouts(new Set());
            setPendingPayoutAmounts(0);
        }
    };

    // Bulk selection
    const handleSelectPayout = (payoutId) => {
        const newSelected = new Set(selectedPayouts);
        if (newSelected.has(payoutId)) {
            newSelected.delete(payoutId);
        } else {
            newSelected.add(payoutId);
        }
        setSelectedPayouts(newSelected);
    };

    // Bulk release
    const handleBulkRelease = async () => {
        if (selectedPayouts.size === 0) {
            alert('Please select at least one pending payout to release');
            return;
        }
        setConfirmRelease(true);
    };

    // Bulk release process
    const processBulkRelease = async () => {
        setConfirmRelease(false);
        setIsProcessing(true);

        try {
            const payoutIds = Array.from(selectedPayouts);
            const response = await axios.put(
                `${process.env.REACT_APP_API_URL}/admin/affiliate/release/bulk-payouts`,
                { payout_ids: payoutIds },
                {
                    headers: {
                        Signature: `${process.env.REACT_APP_API_SIGNATURE}`,
                        Authorization: `Bearer ${token}`,
                    },
                }
            );

            setSelectedPayouts(new Set());
            fetchPayouts(currentPage, searchStatus, searchPhone, searchName, searchEmail);
            toast.success(response.data.message);
            fetchPayoutAmounts();
        } catch (error) {
            console.error('Error releasing bulk payouts:', error);
            toast.error('Failed to release some payouts. Please try again.');
        } finally {
            setIsProcessing(false);
        }
    };


    // Pagination
    const handlePageChange = (page) => {
        setCurrentPage(page);
    };

    // Payout modal
    const handlePayoutClick = (payout) => {
        setSelectedPayout(payout);
        setShowModal(true);
    };

    // // Release payout
    // const handleReleasePayment = async () => {
    //     try {
    //         await axios.post(`${process.env.REACT_APP_API_URL}/admin/affiliate/release/payouts`, { payout_id: selectedPayout.id }, {
    //             headers: {
    //                 Signature: `${process.env.REACT_APP_API_SIGNATURE}`,
    //                 Authorization: `Bearer ${token}`,
    //             },
    //         });
    //         setShowModal(false);
    //         fetchPayouts(currentPage, searchStatus, searchName, searchEmail, searchPhone);
    //     } catch (error) {
    //         console.error('Error releasing payment:', error);
    //     }
    // };

    // Fetch payout amounts
    const fetchPayoutAmounts = async () => {
        try {
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/admin/payouts/amounts`, {
                headers: {
                    Signature: `${process.env.REACT_APP_API_SIGNATURE}`,
                    Authorization: `Bearer ${token}`,
                },
            });
            setPayoutAmounts(response.data);
        } catch (error) {
            console.error('Error fetching payout amounts:', error);
        }
    };


    return (
        <>
            <Helmet>
                <title>Manage Payouts - Enervybe</title>
            </Helmet>
            <div className="card">
                <div className="card-header align-items-center border-bottom">
                    <div className="d-flex justify-content-between">
                        <h5 className="card-title">Payouts List</h5>
                        {selectedPayouts.size > 0 && (
                            <button
                                className="btn btn-success"
                                onClick={handleBulkRelease}
                                disabled={isProcessing}
                            >
                                {isProcessing ? 'Processing...' : `Approve Requests (${selectedPayouts.size})`}
                            </button>
                        )}
                    </div>
                </div>

                <div className="card-header p-0">
                    <div className="nav-align-top">
                        <ul className="nav nav-tabs nav-fill" role="tablist">
                            <li className="nav-item border" role="presentation">
                                <button
                                    type="button"
                                    className="nav-link waves-effect active"
                                    role="tab"
                                    data-bs-toggle="tab"
                                    data-bs-target="#navs-justified-home"
                                    aria-controls="navs-justified-home"
                                    aria-selected="false"
                                    tabIndex={-1}
                                    onClick={() => setSearchStatus('pending')}
                                >
                                    <span className="d-none d-sm-block">
                                        Pending{" "}
                                    </span>
                                    <i className="ri-home-smile-line ri-20px d-sm-none" />
                                </button>
                            </li>
                            <li className="nav-item border" role="presentation">
                                <button
                                    type="button"
                                    className="nav-link waves-effect"
                                    role="tab"
                                    data-bs-toggle="tab"
                                    data-bs-target="#navs-justified-profile"
                                    aria-controls="navs-justified-profile"
                                    aria-selected="true"
                                    tabIndex={-1}
                                    onClick={() => setSearchStatus('completed')}
                                >
                                    <span className="d-none d-sm-block">
                                        Completed

                                    </span>
                                </button>
                            </li>
                            <li className="nav-item border" role="presentation">
                                <button
                                    type="button"
                                    className="nav-link waves-effect"
                                    role="tab"
                                    data-bs-toggle="tab"
                                    data-bs-target="#navs-justified-messages"
                                    aria-controls="navs-justified-messages"
                                    aria-selected="false"
                                    onClick={() => setSearchStatus('failed')}
                                >
                                    <span className="d-none d-sm-block">
                                        Failed
                                    </span>
                                </button>
                            </li>
                        </ul>
                    </div>
                </div>


                {/* Payout */}
                <div className="row g-6 mt-1 px-5">
                    <div className="col-sm-4">
                        <div className="card card-border-shadow-warning h-100">
                            <div className="card-body">
                                <div className="d-flex align-items-center mb-4">
                                    <div className="avatar me-4">
                                        <span className="avatar-initial rounded-3 bg-label-warning">
                                            <i className="ri-arrow-up-line ri-24px" />
                                        </span>
                                    </div>
                                    <div className='d-flex align-items-end'>
                                        {/* <h4 className="mb-0">{payoutAmounts?.pendingCount || 0}/</h4> */}
                                        <h4 className="mb-0">${payoutAmounts?.pendingPayoutAmount || 0.00}</h4>
                                    </div>
                                </div>
                                <h6 className="mb-0 fw-normal">Requested Withdrawals</h6>
                            </div>
                        </div>
                    </div>

                    {/* Last Month Withdrawals */}
                    <div className="col-sm-4">
                        <div className="card card-border-shadow-success h-100">
                            <div className="card-body">
                                <div className="d-flex align-items-center mb-4">
                                    <div className="avatar me-4">
                                        <span className="avatar-initial rounded-3 bg-label-success">
                                            <i className="ri-arrow-up-down-line ri-24px" />
                                        </span>
                                    </div>
                                    <div className='d-flex align-items-end'>
                                        {/* <h4 className="mb-0">{payoutAmounts?.successCount || 0}/</h4> */}
                                        <h4 className="mb-0">${payoutAmounts?.successPayoutAmount || 0.00}</h4>
                                    </div>
                                </div>
                                <h6 className="mb-0 fw-normal">Completed Withdrawals</h6>
                            </div>
                        </div>
                    </div>

                    {/* Failed Withdrawals */}
                    <div className="col-sm-4">
                        <div className="card card-border-shadow-danger h-100">
                            <div className="card-body">
                                <div className="d-flex align-items-center mb-4">
                                    <div className="avatar me-4">
                                        <span className="avatar-initial rounded-3 bg-label-danger">
                                            <i className="ri-arrow-down-line ri-24px" />
                                        </span>
                                    </div>
                                    <div className='d-flex align-items-end'>
                                        {/* <h4 className="mb-0">{payoutAmounts?.failedCount || 0}/</h4> */}
                                        <h4 className="mb-0">${payoutAmounts?.failedPayoutAmount || 0.00}</h4>
                                    </div>
                                </div>
                                <h6 className="mb-0 fw-normal">Failed Withdrawals</h6>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card-datatable text-nowrap">
                    <div className="dataTables_wrapper dt-bootstrap5 no-footer">
                        <div className="row">
                            <div className="col-sm-12 col-md-6">
                                <div className="dataTables_length">
                                    <label>
                                        Show{' '}
                                        <select
                                            name="DataTables_Table_0_length"
                                            aria-controls="DataTables_Table_0"
                                            className="form-select form-select-sm m-2"
                                            value={limit}
                                            onChange={(e) => setLimit(e.target.value)}
                                        >
                                            <option value={5}>5</option>
                                            <option value={10}>10</option>
                                            <option value={25}>25</option>
                                            <option value={50}>50</option>
                                        </select>
                                        entries
                                    </label>
                                </div>
                            </div>
                        </div>

                        {loading ? (
                            <div
                                className="d-flex justify-content-center align-items-center w-100"
                                style={{ height: '100px' }}
                            >
                                <div className="spinner-border" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>) :
                            (< div className="table-responsive">
                                <table className="table table-bordered table-hover">
                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Name</th>
                                            <th>Email</th>
                                            <th>Phone</th>
                                            <th>Amount</th>
                                            <th>Requested On</th>
                                            <th>Payout Date</th>
                                            {searchStatus === 'pending' && <th>Action</th>}
                                        </tr>
                                    </thead>

                                    <tbody>
                                        <tr>
                                            <td></td>
                                            <td>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="search name"
                                                    value={searchName}
                                                    onChange={(e) => setSearchName(e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="search email"
                                                    value={searchEmail}
                                                    onChange={(e) => setSearchEmail(e.target.value)}
                                                />
                                            </td>
                                            <td>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder="search phone"
                                                    value={searchPhone}
                                                    onChange={(e) => setSearchPhone(e.target.value)}
                                                />
                                            </td>
                                            <td></td>
                                            <td></td>
                                            <td></td>
                                            {searchStatus === 'pending' && <td className='d-flex justify-content-center align-items-center py-4'>
                                                <input
                                                    title='Select All'
                                                    className='form-check-input h-max my-3'
                                                    type="checkbox"
                                                    onChange={handleSelectAll}
                                                    checked={selectedPayouts.size > 0 && selectedPayouts.size === payouts.filter(p => p.payout_status === 'pending').length}
                                                />
                                            </td>}
                                        </tr>
                                        {payouts.length === 0 ? (
                                            <tr>
                                                <td colSpan={8} className='text-center'>No {searchStatus} payout found.</td>
                                            </tr>
                                        ) : (
                                            payouts.map((payout, index) => (
                                                <tr key={index}>
                                                    <td>{index + 1}</td>
                                                    <td>{payout.payout_user.first_name + ' ' + payout.payout_user.last_name}</td>
                                                    <td>{payout.payout_user.email}</td>
                                                    <td>{payout.payout_user.phone_no}</td>
                                                    <td>${payout.amount}</td>
                                                    <td>{formatDate(payout.created_at)}</td>
                                                    <td>{payout.payout_date != null ? formatDate(payout.payout_date) : 'Pending'}</td>
                                                    {searchStatus === 'pending' && <td>
                                                        <button
                                                            className="btn btn-sm text-primary border border-primary"
                                                            onClick={() => handlePayoutClick(payout)}
                                                        >
                                                            View
                                                        </button>
                                                    </td>}
                                                </tr>)
                                            ))}
                                    </tbody>
                                </table>
                            </div>)}

                        {/* Pagination Controls */}
                        <div className="row">
                            <div className='d-flex justify-content-start'>
                                <div className="dataTables_paginate">
                                    <ul className="pagination">
                                        <li
                                            className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}
                                        >
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(currentPage - 1)}
                                            >
                                                <i className='tf-icon ri-skip-back-mini-line ri-20px'></i>
                                            </button>
                                        </li>
                                        {[...Array(pagination.totalPages)].map((_, index) => (
                                            <li
                                                key={index}
                                                className={`page-item ${currentPage === index + 1 ? 'active' : ''}`}
                                            >
                                                <button
                                                    className="page-link"
                                                    onClick={() => handlePageChange(index + 1)}
                                                >
                                                    {index + 1}
                                                </button>
                                            </li>
                                        ))}
                                        <li
                                            className={`page-item ${currentPage === pagination.totalPages ? 'disabled' : ''}`}
                                        >
                                            <button
                                                className="page-link"
                                                onClick={() => handlePageChange(currentPage + 1)}
                                            >
                                                <i className='tf-icon ri-skip-forward-mini-line ri-20px'></i>
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div >

            {/* Modal for Payout Details */}
            {
                showModal && selectedPayout && (
                    <div className="modal show d-block" tabIndex="-1" aria-labelledby="payoutModalLabel" aria-hidden="true">
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header border-bottom pb-2">
                                    <h5 className="modal-title" id="payoutModalLabel">
                                        Payout Details
                                    </h5>
                                    <button
                                        type="button"
                                        className="btn-close"
                                        onClick={() => setShowModal(false)}
                                        aria-label="Close"
                                    ></button>
                                </div>
                                <div className="modal-body">
                                    <p><strong>User:</strong> {selectedPayout.payout_user.first_name + ' ' + selectedPayout.payout_user.last_name}</p>
                                    <p><strong>Email:</strong> {selectedPayout.payout_user.email}</p>
                                    <p><strong>Phone:</strong> {selectedPayout.payout_user.phone_no}</p>
                                    <p><strong>Amount: <span className='fs-5'>${selectedPayout.amount}</span></strong></p>
                                    <p><strong>Status:</strong> {selectedPayout.payout_status}</p>
                                    <p><strong>Date:</strong> {formatDate(selectedPayout.created_at)}</p>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>
                                        Close
                                    </button>
                                    {/* {selectedPayout.payout_status === 'pending' && <button type="button" className="btn btn-success" onClick={handleReleasePayment}>
                                    Release Payment
                                </button>} */}
                                </div>
                            </div>
                        </div>
                    </div>
                )
            }
            {/* Modal for Bulk Release */}
            {
                confirmRelease &&
                <>
                    <div className="modal show" style={{ display: 'block' }}>
                        <div className="modal-dialog">
                            <div className="modal-content">
                                <div className="modal-header">
                                    <h5 className="modal-title">Confirm Release</h5>
                                    <button type="button" className="btn-close" onClick={() => setConfirmRelease(false)}></button>
                                </div>
                                <div className="modal-body">
                                    <p className="mb-0">Are you sure you want to approve all the selected withdrawal requests?</p>
                                    <p className="mt-2 mb-0 fw-bold">The total released amount:${pendingPayoutAmounts.toFixed(2)}</p>
                                </div>
                                <div className="modal-footer">
                                    <button type="button" className="btn btn-secondary" onClick={() => setConfirmRelease(false)}>
                                        Cancel
                                    </button>
                                    <button type="button" className="btn btn-primary" onClick={processBulkRelease} disabled={isProcessing}>
                                        {isProcessing ? 'Processing...' : 'Confirm to Pay'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </>
            }
        </>
    );
}



export default Payouts;